import { getNotificationActions } from './actions/notifications';
import { getQuestionActions } from './actions/questions';
import { getPurchaseActions } from './actions/purchases';
import { getSaleActions } from './actions/sales';
import { getOfferingActions } from './actions/offerings';
import { getPurchaseTicketsActions } from './actions/purchaseTickets';
import { getSaleTicketsActions } from './actions/saleTickets';
import { getPurchaseLandsListActions } from './actions/purchaseLands';
import { getSaleLandsListActions } from './actions/saleLands';
import { getCategoryActions } from './actions/categories';
import { getProfileActions } from './actions/profile';
import { getLandActions } from './actions/lands';
import { getPaymentMethodActions } from './actions/paymentMethods';
import { getReviewActions } from './actions/review';
import { getReviewStarActions } from './actions/reviewStars';
import { getOfferingVideoActions } from './actions/offeringVideos';
import { getWantedActions } from './actions/wanteds';
import { getSupplyProposalActions } from './actions/supplyProposals';
import history from '../history';
import {
  insertAction,
  updateAction,
} from '../simpleStore/simpleActions/simpleActions';
import { HitchpinApi } from '../api/Hitchpin';
import { appendDictionaryAction } from '../simpleStore/simpleActions/simpleDictionaryActions';

const isPublic = window.document
  ? window.document.domain.match(/hitchpin\.com/i)
  : true;

const wsDebug = false;
const debug = (...args) => {
  if (wsDebug && !isPublic) {
    console.log(...args);
  }
};

const mutableReferences = {
  socket: null,
  store: null,
  retryIndex: 0,
  openSockets: 0,
};

// Returns number of milliseconds we should wait before retrying to connect.
const fallOffFunctionMilliseconds = retryIndex => {
  if (retryIndex < 1) {
    return 100;
  }
  return Math.round(Math.min(40, 1.3 ** retryIndex) * 1000);
};

const attemptReconnect = retryAction => {
  if (mutableReferences.openSockets) {
    return;
  }

  setTimeout(() => {
    debug(
      `Performing retry index ${
        mutableReferences.retryIndex
      } after ${fallOffFunctionMilliseconds(mutableReferences.retryIndex)}ms.`,
    );
    mutableReferences.retryIndex += 1;
    retryAction();
  }, fallOffFunctionMilliseconds(mutableReferences.retryIndex));
};

const checkIfOpenOrReconnect = isClosedHandler => {
  switch (mutableReferences.socket.readyState) {
    case WebSocket.CONNECTING: {
      debug('Still connecting');
      setTimeout(() => checkIfOpenOrReconnect(isClosedHandler), 200);
      break;
    }
    case WebSocket.OPEN: {
      debug('The socket is open. Yay.');
      mutableReferences.retryIndex = 0;
      break;
    }
    case WebSocket.CLOSING: {
      debug('The socket is closing.  Boo.');
      setTimeout(() => checkIfOpenOrReconnect(isClosedHandler), 200);
      break;
    }
    case WebSocket.CLOSED: {
      debug('The socket is closed. Sad. The onclose handler will retry.');
      break;
    }
    default: {
      // Do nothing
    }
  }
};

const configureWebsocketService = store => {
  mutableReferences.store = store;
};

const idempotentCleanupWebsocketService = () => {
  // Idempotent cleanup function
  const { socket } = mutableReferences;
  if (socket) {
    socket.close();
    mutableReferences.socket = null;
  }
};

const idempotentStartWebsocketService = () => {
  // Idempotent start function
  idempotentCleanupWebsocketService();

  const { store } = mutableReferences;
  const token = window.localStorage.getItem('token');

  if (!token) {
    return;
  }

  const socket = new WebSocket(
    `wss://${process.env.REACT_APP_API_URL.slice(
      8,
    )}ws/notifications/?key=${token}`,
  );
  debug('My new WS object', socket);

  setTimeout(() => {
    checkIfOpenOrReconnect(() =>
      attemptReconnect(idempotentStartWebsocketService),
    );
    store.dispatch(insertAction('Websockets', { isInitialized: true }));
  }, 100);

  socket.onopen = () => {
    mutableReferences.openSockets += 1;
    if (!isPublic) {
      debug('WS Connection Open');
    }
  };

  socket.onmessage = async e => {
    if (!isPublic) {
      debug(JSON.parse(e.data));
    }

    const { url, state, data } = JSON.parse(e.data);
    const tokens = url.split('/').slice(1, -1);

    const endpoint = tokens.filter(x => Number.isNaN(Number(x))).join('-');
    const ids = tokens.filter(x => !Number.isNaN(Number(x))).map(Number);

    const actionMapping = {
      'auth-user': null,
      builds: null,
      categories: getCategoryActions.pending,
      'device-apns': null,
      'device-gcm': null,
      lands: getLandActions.pending,
      notifications: getNotificationActions.pending,
      offerings: getOfferingActions.pending,
      'offerings-images': null,
      'offerings-videos': getOfferingVideoActions.pending,
      'offerings-questions': getQuestionActions.pending,
      organizations: null,
      'organizations-announcements': null,
      'organizations-users': null,
      'payment-methods': getPaymentMethodActions.pending,
      payments: null,
      profile: getProfileActions.pending,
      purchases: getPurchaseActions.pending,
      'purchases-tickets': getPurchaseTicketsActions.pending,
      reviews: getReviewActions.pending,
      'reviews-stars': getReviewStarActions.pending,
      sales: getSaleActions.pending,
      // 'sales-lands': getSaleLandsActions.pending,
      'sales-tickets': getSaleTicketsActions.pending,
      wanteds: getWantedActions.pending,
      'supply-proposals': getSupplyProposalActions.pending,
    };

    if (actionMapping[endpoint] && store && store.dispatch) {
      setTimeout(() => {
        store.dispatch(actionMapping[endpoint](...ids));
      }, 750);
    }

    if (
      state === 'modified' &&
      endpoint === 'profile' &&
      data &&
      data?.is_active === false
    ) {
      history.push('/accounts/logout');
    }

    if (endpoint === 'chats-messages' && store && store.dispatch) {
      if (
        (state === 'created' || state === 'modified') &&
        data.approved === true
      ) {
        const userId = store.getState().user.id;
        if (userId !== data.user) {
          const getUnreadResponse = await HitchpinApi.chats.getUnread();
          const count = getUnreadResponse?.body?.count;
          if (count !== null) {
            const lastUpdatedDateTime = new Date().toLocaleString();
            store.dispatch(
              insertAction('UnreadChatCount', {
                count,
                lastUpdated: lastUpdatedDateTime,
                isLoading: false,
              }),
            );
          }
          // Update chat store
          const incomingChatId = ids[0];

          const chatsStore = store.getState()?.chats;
          const existingChat = chatsStore?.results.filter(
            chat => chat.id === incomingChatId,
          )[0];
          if (!existingChat && chatsStore.total >= 0) {
            const getSingleResponse = await HitchpinApi.chats.getSingle(
              incomingChatId,
            );
            store.dispatch(
              insertAction('Chats', {
                results: [getSingleResponse.body],
                total: chatsStore.total + 1,
              }),
            );
          } else if (existingChat) {
            existingChat.latest_message = data.timestamp;
            store.dispatch(updateAction('Chats', existingChat));
          }

          // Update chat messages store
          const postPayload = data;

          const storeName = 'ChatMessages';
          store.dispatch(
            appendDictionaryAction(storeName, incomingChatId, postPayload),
          );
        }
      }
    }

    if (endpoint === 'chats-users' && store && store.dispatch) {
      const incomingChatId = ids[0];
      const chatsStore = store.getState()?.chats;
      const existingChat = chatsStore?.results.filter(
        chat => chat.id === incomingChatId,
      )[0];
      if (existingChat) {
        const isUpdateForLoggedChatUser = existingChat.user.user === data.user;
        const indexOfOtherUser = existingChat.other_users.findIndex(
          otherUser => otherUser.user === data.user,
        );
        if (isUpdateForLoggedChatUser) {
          existingChat.user = data;
        } else if (indexOfOtherUser !== -1) {
          existingChat.other_users[indexOfOtherUser] = data;
        } else {
          existingChat.other_users.concat(data);
        }
        store.dispatch(updateAction('Chats', existingChat));
      }
    }

    if (endpoint === 'purchases-lands' && store && store.dispatch) {
      setTimeout(() => {
        store.dispatch(getPurchaseLandsListActions.pending(ids[0]));
      }, 750);
    }

    if (endpoint === 'sales-lands' && store && store.dispatch) {
      setTimeout(() => {
        store.dispatch(getSaleLandsListActions.pending(ids[0]));
      }, 750);
    }
  };

  socket.onclose = () => {
    mutableReferences.openSockets = Math.max(
      mutableReferences.openSockets - 1,
      0,
    );

    debug('WS close handler');
    if (!mutableReferences.openSockets) {
      attemptReconnect(idempotentStartWebsocketService);
    }
  };

  socket.onerror = () => {
    debug('WS error handler');
  };

  mutableReferences.socket = socket;
};

// const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
// connection.addEventListener('change', () => { debug(connection) })

// We want to make sure we restart the socket on every change in authentication status
// and on initial load of the website.

export {
  idempotentStartWebsocketService,
  idempotentCleanupWebsocketService,
  configureWebsocketService,
};
